export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

export function haversineKm(a: LatLng, b: LatLng): number {
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function hasCoords(b: { lat?: unknown; lng?: unknown }): b is LatLng {
  return typeof b.lat === 'number' && typeof b.lng === 'number' && Number.isFinite(b.lat) && Number.isFinite(b.lng);
}

export const byGreekName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'el');

/** Nearest first; branches without coordinates go last, in Greek alphabetical order. */
export function sortByDistance<T extends { name: string; lat?: number; lng?: number }>(
  branches: T[],
  here: LatLng,
): Array<T & { distanceKm?: number }> {
  const located = branches
    .filter(hasCoords)
    .map((b) => ({ ...b, distanceKm: haversineKm(here, b as LatLng) }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
  const rest = branches.filter((b) => !hasCoords(b)).sort(byGreekName);
  return [...located, ...rest];
}

export function formatKm(km: number, lang: 'gr' | 'en'): string {
  const digits = km < 10 ? 1 : 0;
  const n = km.toLocaleString(lang === 'en' ? 'en-GB' : 'el-GR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return lang === 'en' ? `${n} km` : `${n} χλμ.`;
}
